import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppActions } from "../../contexts/AppActionsContext";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { requireTenantBandContext } from "../../tenantContext";
import { cacheSongAsBlob } from "../../utils/offlineCache";
import BulkUploadForm from "../BulkUploadForm";
import ConfirmModal from "../ConfirmModal";

const STEPS = ["Upload", "Lock In", "Ready", "Live"];

const StepDots = ({ current, total }) => (
  <div className="flex gap-1.5 mb-6">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className={[
          "h-[2px] w-7 rounded-sm transition-[background] duration-300",
          i <= current ? "bg-brand-gold" : "bg-[#2a2a2a]",
        ].join(" ")}
      />
    ))}
  </div>
);

function defaultSetlistName() {
  const d = new Date();
  return `Rehearsal Set – ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

// ── Step 1: Upload ────────────────────────────────────────────────────────────

function Step1Upload({ tenantContext, currentSongCount, songLimit, onLimitReached, onSaved, onContinue }) {
  const formRef = useRef(null);
  const [readyCount, setReadyCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  return (
    <>
      <h2 className="text-2xl font-semibold text-text-primary mb-1">
        Upload your set material
      </h2>
      <p className="text-sm text-text-secondary mb-6">
        Drop in PDFs of your charts. We'll handle titles.
      </p>

      <BulkUploadForm
        ref={formRef}
        mode="minimal"
        autoTitleFromFilename
        tenantContext={tenantContext}
        onSaved={onSaved}
        currentSongCount={currentSongCount}
        songLimit={songLimit}
        onLimitReached={onLimitReached}
        onClose={onContinue}
        onReadyCountChange={(count, saving) => {
          setReadyCount(count);
          setIsSaving(saving);
        }}
      />

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={() => formRef.current?.save()}
          disabled={readyCount === 0 || isSaving}
          className="border-transparent bg-accent text-white hover:bg-accent/85 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? "Saving…" : "Continue"}
        </button>
      </div>
    </>
  );
}

// ── Step 2: Lock In ───────────────────────────────────────────────────────────

function Step2LockIn({ songs, tenantContext, onComplete, onBack }) {
  const [orderedSongs, setOrderedSongs] = useState(songs);
  const [name, setName] = useState(defaultSetlistName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function onDragEnd(result) {
    if (!result.destination) return;
    const items = Array.from(orderedSongs);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setOrderedSongs(items);
  }

  async function handleLockIn() {
    setIsSubmitting(true);
    setError(null);
    try {
      const { bandId } = requireTenantBandContext(tenantContext);
      const createSetlist = httpsCallable(functions, "createSetlist");
      const finalName = name.trim() || defaultSetlistName();
      const result = await createSetlist({
        bandId,
        name: finalName,
        songIds: orderedSongs.map((s) => s.id),
        performanceDate: null,
      });
      onComplete(result.data.setlistId, finalName, orderedSongs);
    } catch (err) {
      console.error("createSetlist error:", err);
      setError("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <h2 className="text-2xl font-semibold text-text-primary mb-1">
        Lock in your first setlist
      </h2>
      <p className="text-sm text-text-secondary mb-6">
        Start with your opener, then build from there.
      </p>

      <div className="mb-4">
        <label className="block text-xs text-text-muted mb-1.5">Setlist name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent"
        />
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="onboarding-setlist">
          {(provided) => (
            <ul
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="rounded-lg border border-border overflow-hidden mb-4"
            >
              {orderedSongs.map((song, index) => (
                <Draggable key={song.id} draggableId={song.id} index={index}>
                  {(provided, snapshot) => (
                    <li
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={[
                        "group flex items-center gap-2 px-3 py-2.5 border-b border-border last:border-b-0",
                        "transition-[background-color,border-color,box-shadow,opacity] duration-150 transform-gpu",
                        snapshot.isDragging
                          ? "border-accent-blue bg-elevated shadow-lg opacity-95 will-change-transform"
                          : "bg-surface hover:bg-elevated/60",
                      ].join(" ")}
                    >
                      <span
                        {...provided.dragHandleProps}
                        className="shrink-0 cursor-grab select-none text-base leading-none text-text-muted active:cursor-grabbing"
                        title="Drag to reorder"
                      >
                        ⠿
                      </span>
                      <span className="w-5 shrink-0 text-right font-mono text-xs text-text-muted">
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="truncate text-sm font-medium text-text-primary">
                          {song.title}
                        </span>
                        {song.artist && (
                          <span className="ml-2 text-xs text-text-primary/75">
                            {song.artist}
                          </span>
                        )}
                      </span>
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>

      {error && (
        <p role="alert" className="text-[#e05c5c] text-sm mb-4">{error}</p>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="text-sm text-text-secondary border border-border bg-transparent hover:bg-elevated disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleLockIn}
          disabled={isSubmitting || orderedSongs.length === 0}
          className="border-transparent bg-accent text-white hover:bg-accent/85 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" />
              Saving…
            </>
          ) : (
            "Lock in setlist"
          )}
        </button>
      </div>
    </>
  );
}

// ── Step 3: Ready ─────────────────────────────────────────────────────────────

function Step3Ready({ setlistId, songs, tenantContext, onTryLiveMode, onSkipConfirmed }) {
  const [songStatus, setSongStatus] = useState(() =>
    Object.fromEntries(songs.map((s) => [s.id, "pending"]))
  );
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  useEffect(() => {
    songs.forEach((song) => {
      cacheSongAsBlob(setlistId, song, tenantContext)
        .then(() => setSongStatus((prev) => ({ ...prev, [song.id]: "done" })))
        .catch(() => setSongStatus((prev) => ({ ...prev, [song.id]: "failed" })));
    });
    // intentionally runs once on mount — setlistId and songs are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statuses = Object.values(songStatus);
  const totalDone = statuses.filter((s) => s === "done").length;
  const totalFailed = statuses.filter((s) => s === "failed").length;
  const total = songs.length;
  const allSettled = totalDone + totalFailed === total;
  const allCached = totalDone === total;
  const failedSongs = songs.filter((s) => songStatus[s.id] === "failed");

  return (
    <>
      <h2 className="text-2xl font-semibold text-text-primary mb-1">
        Your setlist is ready for rehearsal.
      </h2>
      <p className="text-sm text-text-secondary mb-6">
        No signal required. No surprises mid-song.
      </p>

      <p className="text-sm text-text-secondary mb-5">
        {total} {total === 1 ? "song" : "songs"} in your setlist
      </p>

      {/* Cache progress */}
      {!allSettled ? (
        <div className="flex items-center gap-2.5 text-sm text-text-secondary mb-6">
          <span
            className="w-3.5 h-3.5 shrink-0 border-2 border-text-muted/30 border-t-text-muted rounded-full animate-spin"
            aria-hidden="true"
          />
          Caching {totalDone} of {total} charts for offline use…
        </div>
      ) : allCached ? (
        <div className="flex items-center gap-2 text-sm text-accent-green mb-6">
          <span aria-hidden="true">✓</span>
          Offline ready ✓
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-amber-400 mb-6">
          <span aria-hidden="true">⚠</span>
          {totalDone} of {total} cached. Live Mode may need signal for{" "}
          {failedSongs.map((s) => s.title).join(", ")}.
        </div>
      )}

      <p className="text-xs text-text-muted mb-6">
        This is exactly how it will work on stage.
      </p>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={onTryLiveMode}
          disabled={!allSettled}
          className="w-full border-transparent bg-brand-gold text-[#0d0d0c] font-semibold hover:bg-brand-gold/85 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Try Live Mode
        </button>
        <button
          type="button"
          onClick={() => setShowSkipConfirm(true)}
          className="text-sm text-text-muted underline bg-transparent border-0 cursor-pointer hover:text-text-secondary self-center"
        >
          Skip for now
        </button>
      </div>

      <ConfirmModal
        open={showSkipConfirm}
        title="Skip Live Mode setup?"
        message="You can finish setup later, but Live Mode is what makes this work on stage."
        confirmLabel="Skip anyway"
        cancelLabel="Go back"
        danger={false}
        onConfirm={onSkipConfirmed}
        onCancel={() => setShowSkipConfirm(false)}
      />
    </>
  );
}

// ── Flow shell ────────────────────────────────────────────────────────────────

export default function OnboardingFlow({
  bandId,
  tenantContext,
  songs,
  setlists,
  isSongsLoading,
  isSetlistsLoading,
  currentSongCount,
  songLimit,
  onLimitReached,
  onSaved,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { startLiveModeForSetlist } = useAppActions() ?? {};
  const returnTo = searchParams.get("returnTo") || "/";

  // null = still deriving; set by the resume effect below
  const [step, setStep] = useState(null);
  const [uploadedSongs, setUploadedSongs] = useState([]);
  const [setlistId, setSetlistId] = useState(null);
  const [setlistName, setSetlistName] = useState("");
  const [setlistSongs, setSetlistSongs] = useState([]);
  const initializedRef = useRef(false);

  // Derive the starting step from Firestore data once loading is done.
  // Runs at most once — the ref guard prevents re-derivation on subsequent
  // data updates (e.g., after a song is saved during the wizard session).
  useEffect(() => {
    if (isSongsLoading || isSetlistsLoading) return;
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (localStorage.getItem(`bos_onboarding_complete_${bandId}`) === "true") {
      navigate(returnTo, { replace: true });
      return;
    }
    if (localStorage.getItem(`bos_live_mode_entered_${bandId}`) === "true") {
      if (bandId) localStorage.setItem(`bos_onboarding_complete_${bandId}`, "true");
      navigate(returnTo, { replace: true });
      return;
    }
    if (setlists.length > 0) {
      const sorted = [...setlists].sort(
        (a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0)
      );
      const latest = sorted[0];
      const latestSongs = (latest.songIds ?? [])
        .map((id) => songs.find((s) => s.id === id))
        .filter(Boolean);
      setSetlistId(latest.id);
      setSetlistName(latest.name ?? "");
      setSetlistSongs(latestSongs);
      setStep(2);
      return;
    }
    if (songs.length > 0) {
      setUploadedSongs([...songs]);
      setStep(1);
      return;
    }
    setStep(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSongsLoading, isSetlistsLoading]);

  const handleExit = () => {
    if (bandId) localStorage.setItem(`bos_onboarding_dismissed_${bandId}`, "true");
    navigate(returnTo, { replace: true });
  };

  const handleMarkComplete = () => {
    if (bandId) {
      localStorage.setItem(`bos_onboarding_complete_${bandId}`, "true");
    }
    navigate(returnTo, { replace: true });
  };

  // Intercept onSaved to accumulate songs for Step 2
  const handleSongSaved = (song) => {
    onSaved(song);
    setUploadedSongs((prev) => [...prev, song]);
  };

  const isLastStep = step === STEPS.length - 1;

  function renderStep() {
    switch (step) {
      case 0:
        return (
          <Step1Upload
            tenantContext={tenantContext}
            currentSongCount={currentSongCount}
            songLimit={songLimit}
            onLimitReached={onLimitReached}
            onSaved={handleSongSaved}
            onContinue={() => setStep(1)}
          />
        );
      case 1:
        return (
          <Step2LockIn
            key={uploadedSongs.length}
            songs={uploadedSongs}
            tenantContext={tenantContext}
            onComplete={(id, name, orderedSongs) => {
              setSetlistId(id);
              setSetlistName(name);
              setSetlistSongs(orderedSongs);
              setStep(2);
            }}
            onBack={() => setStep(0)}
          />
        );
      case 2:
        return (
          <Step3Ready
            setlistId={setlistId}
            songs={setlistSongs}
            tenantContext={tenantContext}
            onTryLiveMode={() => {
              if (bandId) {
                localStorage.setItem(`bos_live_mode_entered_${bandId}`, "true");
                localStorage.setItem(`bos_onboarding_complete_${bandId}`, "true");
              }
              startLiveModeForSetlist?.(setlistId, setlistName, setlistSongs);
              navigate("/?coachmark=live", { replace: true });
            }}
            onSkipConfirmed={handleMarkComplete}
          />
        );
      default:
        return (
          <>
            <h2 className="text-2xl font-semibold text-text-primary mb-8">
              Step {step + 1}
            </h2>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="text-sm text-text-secondary border border-border bg-transparent hover:bg-elevated"
              >
                Back
              </button>
              {isLastStep ? (
                <button
                  type="button"
                  onClick={handleMarkComplete}
                  className="border-transparent bg-accent text-white hover:bg-accent/85"
                >
                  Mark complete &amp; exit
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.min(s + 1, STEPS.length - 1))}
                  className="border-transparent bg-accent text-white hover:bg-accent/85"
                >
                  Next
                </button>
              )}
            </div>
          </>
        );
    }
  }

  return (
    <div className="min-h-screen bg-base flex flex-col">
      {/* Header — always visible so there's no full-blank flash during derive */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <span className="text-sm font-medium text-text-secondary">Band OS Setup</span>
        <button
          type="button"
          onClick={handleExit}
          className="text-xs text-text-muted hover:text-text-secondary border-0 bg-transparent p-0"
        >
          Exit setup
        </button>
      </div>

      {/* Body — only rendered once the starting step is known */}
      {step !== null && (
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
          <div className="w-full max-w-lg">
            <StepDots current={step} total={STEPS.length} />
            {renderStep()}
          </div>
        </div>
      )}
    </div>
  );
}
